import {
  AsyncPipe,
  Location,
  NgClass,
  NgComponentOutlet,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  inject,
  Injector,
  type OnDestroy,
  reflectComponentType,
  signal,
  type Type,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToolType } from '@bizzdesign/bizzdesign-sdk';
import { deriveUUID, type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import { ContentDetailsGlobalStore } from '@bizzdesign/data-access-content-details';
import { ElementCollectionService } from '@bizzdesign/data-access-element-collections';
import {
  type FrameBase,
  type FrameItem,
  InvalidAssetIdError,
  isAdhocFrame,
  isAdhocFrameBase,
  isFrame,
  isWorkspaceFrameBase,
  isWorkspaceFrameItem,
  isWorkspaceItem,
  noContextErrorFactory,
  type ViewItemBase,
  type WorkspaceBase,
  type WorkspaceFrame,
  type WorkspaceFrameItem,
  type WorkspaceFrameUpdate,
  type WorkspaceItem,
  WorkspacesService,
  type WorkspaceUpdate,
} from '@bizzdesign/data-access-workspaces';
import {
  ContentOpenContainerComponent,
  type ContentOpenDialogChoice,
  ContentOpenService,
  CreateWorkspaceFrameNavigationState,
  DuplicateWorkspaceFrameNavigationState,
  newWorkspace,
  WorkspaceFrameNavigationState,
  WorkspaceNavigationState,
} from '@bizzdesign/feature-content-open';
import { WorkspaceInviteContainerComponent } from '@bizzdesign/feature-invite';
import {
  dialogWorkspacePickerUiEvents,
  frameCreationSnackbarUiEvents,
  frameToolUiEvents,
} from '@bizzdesign/modeler/api-workspaces/events';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  type ActionButton,
  type BDMenuItem,
  type ContentItem,
  DefaultDialogSize,
  DialogProperties,
  type ElementCollection,
  type Page,
  TemplateType,
  type ToolContainer,
  type ToolContainerInputs,
  type ToolContentUpdate,
  type ToolContentUpdateNotice,
  type ToolData,
  type ToolEvent,
  type ToolInputsFactory,
} from '@bizzdesign/shared/models';
import { HighlightTextPipe } from '@bizzdesign/shared/pipes';
import {
  AccountToolbarService,
  LayoutContextService,
  NavigationToolbarService,
  OsDetectorService,
  ProductAnalyticsService,
  UserActivityTrackingService,
} from '@bizzdesign/shared/services';
import { baseRoutes } from '@bizzdesign/shared/utils';
import { CallToActionComponent } from '@bizzdesign/ui-call-to-action';
import { BackdropComponent, DialogComponent } from '@bizzdesign/ui-components';
import { HelperTextComponent } from '@bizzdesign/ui-helper-text';
import { LoaderComponent } from '@bizzdesign/ui-loader';
import {
  TOOL_SPEC_REGISTER,
  type ToolSpecRegister,
} from '@bizzdesign/workspaces/feature-common';
import { TranslocoService } from '@jsverse/transloco';
import { Dispatcher } from '@ngrx/signals/events';
import { AutoFocusModule } from 'primeng/autofocus';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import {
  BehaviorSubject,
  buffer,
  catchError,
  combineLatest,
  combineLatestWith,
  concat,
  concatMap,
  debounce,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  firstValueFrom,
  from,
  ignoreElements,
  map,
  merge,
  mergeMap,
  type Observable,
  of,
  ReplaySubject,
  scan,
  shareReplay,
  Subject,
  switchMap,
  take,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';
import { FrameContext } from '../../resolvers/frame-context-resolver';
import {
  arrangeMenuItems,
  displayMenuItems,
  displayMenuItemsPublishedView,
  editMenuItems,
  frameMenuItems,
  modelingAssistanceMenuItems,
  resolveCallbackForDesignType,
  toolsMenuItems,
} from './frame-container.component.util';

interface PageData {
  frameId: UUID;
  frameName: string;
  toolType: ToolType;
  toolContainerType: Type<ToolContainer>;
  toolContainerInputs: Partial<ToolContainerInputs>;
  isFrame: boolean;
  isAdhocFrame: boolean;
  isWorkspaceFrame: boolean;
  workspace?: WorkspaceBase;
  archived: boolean;
  viewItem: ViewItemBase | undefined;
}

interface ExtendedUpdateNotice {
  id: string;
  toolType: ToolType;
  name?: string;
  forceFlush?: boolean;
  timestamp?: unknown;
  getContentUpdate?: (timestamp?: unknown) => Promise<ToolContentUpdate | null>;
}

const RECENT_SIBLINGS_LIMIT = 3;
const ALL_FRAMES_PAGE_SIZE = 20;

@Component({
  selector: 'bd-frame-container',
  imports: [
    AsyncPipe,
    NgClass,
    AutoFocusModule,
    FormsModule,
    BackdropComponent,
    DialogComponent,
    LoaderComponent,
    CallToActionComponent,
    NgComponentOutlet,
    InputTextModule,
    HelperTextComponent,
    ContentOpenContainerComponent,
    SharedI18nModule,
    HighlightTextPipe,
    WorkspaceInviteContainerComponent,
    ButtonModule,
    CallToActionComponent,
  ],
  providers: [Dispatcher],
  templateUrl: './frame-container.component.html',
  styleUrls: ['./frame-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FrameContainerComponent implements OnDestroy {
  private readonly dispatcher = inject(Dispatcher);
  private readonly workspacesService = inject(WorkspacesService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly navigationToolbarService = inject(NavigationToolbarService);
  private readonly accountToolbarService = inject(AccountToolbarService);
  private readonly translocoService = inject(TranslocoService);
  private readonly contentOpenService = inject(ContentOpenService);

  private readonly injector = inject(Injector);
  private readonly toolSpecRegister =
    inject<ToolSpecRegister>(TOOL_SPEC_REGISTER);
  private readonly contentDetailsStore = inject(ContentDetailsGlobalStore);
  private readonly productAnalyticsService = inject(ProductAnalyticsService);
  private readonly osDetectorService = inject(OsDetectorService);
  private readonly userActivityTrackingService = inject(
    UserActivityTrackingService,
  );
  private readonly destroyRef = inject(DestroyRef);

  private readonly updateNotice = new ReplaySubject<ExtendedUpdateNotice>();
  private readonly updateCount = new BehaviorSubject<number>(0);
  private readonly noticeCount = new BehaviorSubject<number>(0);
  private readonly workspaceUpdateCount = new BehaviorSubject<number>(0);
  private readonly workspaceNoticeCount = new BehaviorSubject<number>(0);
  private readonly bufferFlush = new Subject<void>();
  private readonly workspaceUpdate = new Subject<WorkspaceUpdate>();
  private readonly viewPublish = new Subject<{
    id: UUID;
    name: string;
    preview?: string;
  }>();
  changeNameDialogVisible$ = new BehaviorSubject<boolean>(false);
  contentOpenDialogVisible$ = new BehaviorSubject<boolean>(false);
  workspacePickerDialogVisible$ = new BehaviorSubject<boolean>(false);
  deleteFrameDialogVisible$ = new BehaviorSubject<boolean>(false);
  inviteDialogVisible$ = new BehaviorSubject<boolean>(false);
  inviteDialogButton$ = new BehaviorSubject<boolean>(true);
  destroyed$ = new Subject<void>();
  changeNameDialogHeaderKey$ = new BehaviorSubject<string>('designChangeName');
  private frame: FrameBase | undefined;
  private readonly layoutContextService = inject(LayoutContextService);
  layoutContext$ = this.layoutContextService.context$;

  elementIdsSelectionSubject$ = new BehaviorSubject<UUID[]>([]);
  elementCollectionLoadingSubject$ = new BehaviorSubject<boolean>(false);
  elementCollectionService = inject(ElementCollectionService);

  elementCollection$: Observable<ElementCollection> =
    this.elementIdsSelectionSubject$.pipe(
      tap(() => this.elementCollectionLoadingSubject$.next(true)),
      debounce(() => this.contentOpenDialogVisible$.pipe(filter((v) => v))),
      switchMap((ids) => {
        return ids.length
          ? this.elementCollectionService.getCollection(ids)
          : of({ objects: [], relations: [] });
      }),
      tap(() => this.elementCollectionLoadingSubject$.next(false)),
    );

  inlineNotificationVisible = signal<boolean>(false);
  changeNameValue = signal<string | undefined>(undefined);

  debounceTime = 2000;

  private readonly designFrameSubmenus = (): BDMenuItem[] => [
    { separator: true },
    ...toolsMenuItems(),
    ...editMenuItems(this.editMenuCommandHandler),
    ...arrangeMenuItems(
      this.osDetectorService.getKeyFromCode.bind(this.osDetectorService),
    ),
    ...displayMenuItems(),
    { separator: true },
    ...modelingAssistanceMenuItems(),
  ];

  private readonly editMenuCommandHandler = (
    key: string,
  ): (() => void) | undefined => {
    if (key === 'menu.addTo') {
      return () => {
        return this.contentOpenDialogVisible$.next(true);
      };
    } else {
      return undefined;
    }
  };

  private readonly pinUnpinLabelKey = new BehaviorSubject<string>(
    'menu.pinFrame',
  );
  private readonly frameMenuCommandHandler = (
    frame: WorkspaceFrameItem,
    key: string | undefined,
  ): (() => void) | undefined => {
    switch (key) {
      case 'menu.renameFrame':
        return () => this.changeNameForFrame();
      case 'workspace.inviteButtonText':
        return () => {
          this.productAnalyticsService.sendEvent('workspace_invite_opened', {
            workspace_id: frame.workspace.id,
            location: 'frame_burger_menu',
          });
          this.inviteDialogVisible$.next(true);
        };
      case 'menu.duplicate':
        return () => this.duplicateFrame(frame);
      case 'pinUnpinFrame':
        return () => this.togglePinUnpinFrame(frame);
      case 'menu.deleteFrame':
        return () => this.deleteFrameDialogVisible$.next(true);
      default:
        return undefined;
    }
  };

  private readonly onToolEvent = (event: ToolEvent): void => {
    if (event.key === 'addTo') {
      this.contentOpenDialogVisible$.next(true);
    } else if (event.key === 'objectAddTo') {
      this.contentOpenDialogVisible$.next(true);
      this.elementIdsSelectionSubject$.next([event.payload]);
    } else if (event.key === 'addViewToFrame') {
      this.workspacePickerDialogVisible$.next(true);
    } else if (event.key === 'viewPublish') {
      this.onViewPublish(event.payload);
    } else if (event.key === 'viewNameUpdate') {
      this.onViewNameUpdate(event.payload);
    }
  };

  private onViewPublish(payload: {
    id: UUID;
    name: string;
    preview?: string;
  }): void {
    this.viewPublish.next(payload);
  }

  private onViewNameUpdate(payload: { id: UUID; name: string }): void {
    if (!this.frame || this.frame.id !== payload.id) {
      return;
    }
    if (this.frame.name === payload.name) {
      return;
    }

    this.updateToolbars({ ...this.frame, name: payload.name });
  }

  private readonly frameFromRoute$: Observable<FrameBase> =
    this.route.data.pipe(
      switchMap((data) => data['context']),
      map((context) => {
        if (context instanceof FrameContext) {
          if (context.subject instanceof Error) {
            throw this.frameErrorHandler(context.subject);
          }
          return { subject: context.subject, autoRename: context.autoRename };
        }
        throw this.frameErrorHandler(noContextErrorFactory('frame'));
      }),
      tap(
        ({ subject: frame }) =>
          isWorkspaceFrameBase(frame) &&
          this.location.replaceState(
            this.router
              .createUrlTree(['..', frame.id], {
                relativeTo: this.route,
                queryParamsHandling: 'merge',
              })
              .toString(),
          ),
      ),
      tap(({ subject: frame, autoRename }) => {
        // Create now emits twice (optimistic, then persisted): reset/rename only
        // on a genuinely new frame, but refresh the toolbars on every emission.
        const isNewFrame = frame.id !== this.frame?.id;
        if (isNewFrame) {
          this.resetComponent();
        }
        this.updateToolbars(frame, true /*initialize*/);
        if (isNewFrame) {
          if (autoRename) {
            this.changeNameForFrame();
          }
          this.contentDetailsStore.updateFrameId(frame.id);
        }
      }),
      map(({ subject }) => subject),
      // debounce until all notices resolved
      debounce(() => this.noNotices$),
      catchError(() => EMPTY),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  // Feed the searchable menu; compare by id so name/metadata edits don't
  // refetch — updateToolbars refreshes those.
  readonly siblingFilepathSync$: Observable<void> = this.frameFromRoute$.pipe(
    distinctUntilChanged((a, b) => a.id === b.id),
    switchMap((frame) => {
      if (!isWorkspaceFrameItem(frame)) {
        this.navigationToolbarService.resetSearchableMenu();
        return of<void>(undefined);
      }
      return this.buildSiblingMenuStream$(frame);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private buildSiblingMenuStream$(frame: WorkspaceFrameItem): Observable<void> {
    // Stamp the active frame so recents ordering follows navigation. Without
    // this the tracker only fires on frame create/update.
    this.userActivityTrackingService.updateFrameActivity(
      frame.id,
      frame.workspace.id,
    );

    this.navigationToolbarService.filepathShowMenu$.next(true);
    this.navigationToolbarService.filepathChevronTooltip$.next({
      textKey: 'workspaces.workspaceContent',
      tScope: 'featureWorkspaces',
    });
    this.navigationToolbarService.searchableMenuActiveItemId$.next(frame.id);
    this.navigationToolbarService.searchableMenuSearchPlaceholder$.next({
      textKey: 'workspaces.searchFrames',
      tScope: 'featureWorkspaces',
    });
    this.navigationToolbarService.searchableMenuTopItemsTitle$.next({
      textKey: 'workspaces.recentlyOpened',
      tScope: 'featureWorkspaces',
    });
    this.navigationToolbarService.searchableMenuNoItemsText$.next({
      textKey: 'workspaces.noOtherFrames',
      tScope: 'featureWorkspaces',
    });
    this.navigationToolbarService.searchableMenuFooterAction$.next(
      this.buildCreateFrameAction(),
    );
    this.navigationToolbarService.searchableMenuTopItems$.next([]);
    this.navigationToolbarService.searchableMenuAllItems$.next([]);
    this.navigationToolbarService.searchableMenuHasMore$.next(false);
    this.navigationToolbarService.searchableMenuAllItemsPageSize$.next(
      ALL_FRAMES_PAGE_SIZE,
    );
    this.navigationToolbarService.searchableMenuTopItemsLoading$.next(true);
    this.navigationToolbarService.searchableMenuAllItemsLoading$.next(true);

    const workspaceId = frame.workspace.id;

    // Fetch by tracker IDs directly; workspaces `orderBy: 'lastOpened'` uses
    // updatedAt DESC and can miss frames that weren't recently updated.
    const recents$ = this.userActivityTrackingService
      .recentFramesInWorkspace(workspaceId, {
        // +1 so filtering the active frame still leaves room for the limit.
        limit: RECENT_SIBLINGS_LIMIT + 1,
      })
      .pipe(
        take(1),
        catchError(() => of<UUID[]>([])),
        map((ids) =>
          ids.filter((id) => id !== frame.id).slice(0, RECENT_SIBLINGS_LIMIT),
        ),
        switchMap((ids) => {
          if (ids.length === 0) return of<FrameItem[]>([]);
          // Sort IDs so the workspaces-cache key is stable across reorderings;
          // tracker order is restored by orderByIds after fetch.
          const cacheKeyIds = [...ids].sort((a, b) => a.localeCompare(b));
          return this.workspacesService
            .getItemsForWorkspace(
              workspaceId,
              { ids: cacheKeyIds, pageSize: ids.length, offset: 0 },
              ['parent'],
              'frame',
            )
            .pipe(
              take(1),
              map((page) => this.orderByIds(page.results, ids)),
              catchError(() => of<FrameItem[]>([])),
            );
        }),
        tap((recents) => {
          this.navigationToolbarService.searchableMenuTopItems$.next(
            recents.map((r) => this.toSiblingMenuItem(r, frame)),
          );
          this.navigationToolbarService.searchableMenuTopItemsLoading$.next(
            false,
          );
        }),
      );

    return recents$.pipe(
      switchMap(() => this.buildAllFramesPagedStream$(workspaceId, frame)),
      map(() => undefined),
    );
  }

  // Full pool including active + recents — the searchable menu hides those
  // from the browse view but keeps them searchable.
  private buildAllFramesPagedStream$(
    workspaceId: UUID,
    frame: WorkspaceFrameItem,
  ): Observable<void> {
    let offset = 0;

    return concat(
      of<void>(undefined),
      this.navigationToolbarService.activeSearchableMenuLoadMore$,
    ).pipe(
      concatMap(() => {
        this.navigationToolbarService.searchableMenuAllItemsLoading$.next(true);
        const currentOffset = offset;
        return this.workspacesService
          .getItemsForWorkspace(
            workspaceId,
            {
              pageSize: ALL_FRAMES_PAGE_SIZE,
              offset: currentOffset,
              orderBy: { order: 'name', by: 'ASC' },
            },
            ['parent'],
            'frame',
          )
          .pipe(
            tap(() => {
              offset += ALL_FRAMES_PAGE_SIZE;
            }),
            // null signals a transient page failure — scan preserves the
            // accumulator so has-more/total don't collapse to zero.
            catchError((): Observable<Page<FrameItem> | null> => of(null)),
          );
      }),
      scan(
        (acc, page) =>
          page === null
            ? acc
            : {
                items: [...acc.items, ...page.results],
                totalCount: page.totalCount,
              },
        { items: [] as FrameItem[], totalCount: 0 },
      ),
      tap((state) => {
        this.navigationToolbarService.searchableMenuAllItemsLoading$.next(
          false,
        );
        this.navigationToolbarService.searchableMenuHasMore$.next(
          state.items.length < state.totalCount,
        );
        this.navigationToolbarService.searchableMenuAllItems$.next(
          state.items.map((f) => this.toSiblingMenuItem(f, frame)),
        );
      }),
      map(() => undefined),
    );
  }

  private orderByIds(items: FrameItem[], ids: readonly UUID[]): FrameItem[] {
    const rank = new Map(ids.map((id, index) => [id, index]));
    return [...items].sort(
      (a, b) =>
        (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    );
  }

  private toSiblingMenuItem(
    sibling: FrameItem,
    activeFrame: WorkspaceFrameItem,
  ): BDMenuItem {
    return {
      id: sibling.id,
      label: sibling.name,
      icon:
        sibling.toolType === ToolType.DESIGN
          ? 'ui-design-frame'
          : 'ui-insights-frame',
      preview$: sibling.preview$,
      command: () => {
        this.router.navigate([baseRoutes.frames, sibling.id], {
          state: new WorkspaceFrameNavigationState({
            ...sibling,
            workspace: activeFrame.workspace,
          }),
        });
      },
    };
  }

  private buildCreateFrameAction(): ActionButton {
    return {
      icon: 'ui-plus',
      type: 'asLink',
      size: 'regular',
      labelKey: 'workspaces.newFrame',
      tScope: 'featureWorkspaces',
      onClick: () => {
        if (!this.frame || !isWorkspaceFrameBase(this.frame)) {
          return;
        }
        this.router.navigate([baseRoutes.frames, 'create'], {
          state: new CreateWorkspaceFrameNavigationState(this.frame.workspace),
        });
      },
    };
  }

  private readonly viewItemFromViewPublish$: Observable<ViewItemBase> =
    this.viewPublish.pipe(
      withLatestFrom(
        defer(() => this.viewItem$),
        this.frameFromRoute$.pipe(filter(isWorkspaceFrameBase)),
      ),
      switchMap(([payload, currentViewItem, frame]) => {
        const viewItem$: Observable<ViewItemBase | undefined> = currentViewItem
          ? this.workspacesService
              .updateViewItem(
                {
                  id: currentViewItem.id,
                  name: payload.name,
                  viewId: payload.id,
                  preview: payload.preview,
                },
                frame.id,
                frame.workspace.id,
              )
              .pipe(catchError(() => of(currentViewItem)))
          : from(deriveUUID(payload.id)).pipe(
              switchMap((id) =>
                this.workspacesService.createViewItem(
                  {
                    id,
                    name: payload.name,
                    viewId: payload.id,
                    preview$:
                      payload.preview === undefined
                        ? undefined
                        : of(payload.preview),
                  },
                  frame.id,
                  frame.workspace.id,
                ),
              ),
              catchError(() => of(undefined)),
            );
        return viewItem$.pipe(
          withLatestFrom(this.frameFromRoute$),
          filter(([, currentFrame]) => currentFrame.id === frame.id),
          map(([viewItem]) => viewItem),
        );
      }),
      filter((viewItem): viewItem is ViewItemBase => viewItem !== undefined),
    );

  private readonly frameUpdate$: Observable<WorkspaceFrameUpdate | null> =
    this.updateNotice.pipe(
      tap(() => {
        this.updateCount.next(this.updateCount.value + 1);
        this.noticeCount.next(this.noticeCount.value + 1);
      }),
      // buffer until 2 seconds of inactivity
      // or until a flush event is triggered
      buffer(
        this.updateNotice.pipe(
          debounce((notice) =>
            merge(
              timer(notice.forceFlush ? 0 : this.debounceTime),
              this.bufferFlush,
            ),
          ),
        ),
      ),
      // only proceed if there are updates
      filter((notices) => notices.length > 0),
      // reduce notices into a single notice
      map((notices) => this.reduceUpdateNotices(notices)),
      // resolve update notice just in time
      concatMap((notice) => this.resolveUpdateNotice(notice)),
    );

  private readonly frameFromUpdate$: Observable<WorkspaceFrame> =
    this.frameUpdate$.pipe(
      // mergeMap, not concatMap: the service already orders writes by ticket, so
      // serialising here made each notice wait out the previous save's trips.
      mergeMap((update) => {
        const frame$ =
          update === null
            ? EMPTY // update canceled
            : this.workspacesService.updateWorkspaceFrame({
                ...update,
                preview$:
                  this.frame && isWorkspaceFrameBase(this.frame)
                    ? this.frame.preview$
                    : undefined,
              });
        this.noticeCount.next(this.noticeCount.value - 1);
        // No toast here: this pipe is torn down before a flushed save settles,
        // so `WorkspacesService` reports the failure.
        return frame$.pipe(
          map((result) => {
            if (result instanceof Error) {
              throw this.frameErrorHandler(result);
            }
            return result;
          }),
          // decrease the running updates count
          finalize(() => this.updateCount.next(this.updateCount.value - 1)),
          // complete inner stream on error
          catchError(() => EMPTY),
        );
      }),
      withLatestFrom(this.frameFromRoute$),
      // skip updates that are not for the active route frame
      filter(
        ([frameFromUpdate, frameFromRoute]) =>
          frameFromUpdate.id === frameFromRoute.id,
      ),
      map(([frame]) => frame),
      // update toolbars after each update
      tap((frame) => this.updateToolbars(frame)),
      // debounce until all updates finished
      debounce(() => this.noUpdates$),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  private readonly viewItem$: Observable<ViewItemBase | undefined> = merge(
    this.frameFromRoute$.pipe(
      map((frame) =>
        isWorkspaceFrameBase(frame) ? frame.viewItem : undefined,
      ),
    ),
    this.viewItemFromViewPublish$,
    this.frameFromUpdate$.pipe(
      withLatestFrom(this.frameFromRoute$),
      filter(
        ([frameFromUpdate, frameFromRoute]) =>
          frameFromUpdate.id === frameFromRoute.id,
      ),
      map(([frame]) => frame.viewItem),
      filter((viewItem): viewItem is ViewItemBase => viewItem !== undefined),
    ),
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  private readonly noUpdates$: Observable<boolean> = this.updateCount.pipe(
    filter((count) => count === 0),
    map(() => true),
  );

  private readonly noNotices$: Observable<boolean> = this.noticeCount.pipe(
    filter((count) => count === 0),
    map(() => true),
  );

  private readonly noWorkspaceUpdates$: Observable<boolean> =
    this.workspaceUpdateCount.pipe(
      filter((count) => count === 0),
      map(() => true),
    );

  readonly toolContainerTypeFromRoute$: Observable<Type<ToolContainer>> =
    this.frameFromRoute$.pipe(
      filter(isFrame),
      switchMap((frame) => this.getToolContainerType(frame.toolType)),
    );

  readonly toolContainerInputsFromRoute$: Observable<
    Partial<ToolContainerInputs>
  > = this.frameFromRoute$.pipe(
    filter(isFrame),
    switchMap(async (frame) => {
      const inputsFactory = await this.getToolInputsFactory(frame.toolType);
      const config = inputsFactory.createConfig?.(!isWorkspaceFrameBase(frame));
      const populatedBy = frame.populatedBy;
      const sourceQuery = frame.sourceQuery;
      const data: ToolData = {
        sourceId: frame.id,
        parentId: isWorkspaceFrameBase(frame) ? frame.workspace.id : undefined,
        content$: frame.content$,
        ...(populatedBy ? { populatedBy } : {}),
        ...(sourceQuery ? { sourceQuery } : {}),
      };

      const rawInputs: ToolContainerInputs = {
        ...(config ? { config } : {}),
        frameName: frame.name,
        data,
        onContentUpdateNotice: (notice) =>
          this.onContentUpdateNotice(notice, frame),
        onObjectIdsInUseChange: undefined,
        onElementIdsSelectionChange: (elementIds: UUID[]) =>
          this.onElementIdsSelectionChange(elementIds, frame),
        onToolEvent: (event: ToolEvent) => this.onToolEvent?.(event),
      };
      const componentType = await this.getToolContainerType(frame.toolType);
      return this.filterInputsForComponent(componentType, rawInputs);
    }),
  );

  private readonly toolContainerInputs$: Observable<
    Partial<ToolContainerInputs>
  > = combineLatest([
    this.toolContainerInputsFromRoute$,
    this.toolContainerTypeFromRoute$,
    merge(
      this.frameFromRoute$.pipe(map((frame) => frame.name)),
      this.frameFromUpdate$.pipe(map((frame) => frame.name)),
    ).pipe(distinctUntilChanged()),
  ]).pipe(
    map(([inputs, componentType, frameName]) =>
      this.filterInputsForComponent(
        componentType,
        this.mergeToolContainerInputs(inputs, frameName),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly pageData$: Observable<PageData> = merge(
    this.frameFromRoute$,
    this.frameFromUpdate$.pipe(
      withLatestFrom(this.frameFromRoute$),
      filter(
        ([frameFromUpdate, frameFromRoute]) =>
          frameFromUpdate.id === frameFromRoute.id,
      ),
      map(([frame]) => frame),
    ),
    this.siblingFilepathSync$.pipe(ignoreElements()),
  ).pipe(
    combineLatestWith(
      this.toolContainerTypeFromRoute$,
      this.toolContainerInputs$,
      this.viewItem$,
    ),
    map(
      ([
        frame,
        toolContainerType,
        toolContainerInputs,
        viewItem,
      ]): PageData => ({
        frameId: frame.id,
        toolType: isFrame(frame) ? frame.toolType : ToolType.DESIGN,
        frameName: frame.name,
        // avoid setting a new content instance for a content update
        // to prevent the modeler-wrapper from resetting the modeler
        toolContainerType,
        toolContainerInputs,
        isFrame: isFrame(frame),
        isAdhocFrame: isAdhocFrame(frame),
        isWorkspaceFrame: isWorkspaceFrameBase(frame),
        archived: isWorkspaceFrameBase(frame)
          ? frame.workspace.archived
          : false,
        workspace: isWorkspaceFrameBase(frame) ? frame.workspace : undefined,
        viewItem,
      }),
    ),
  );

  readonly workspace$: Observable<WorkspaceBase> = merge(
    // use workspace from frameFromRoute$ - not from frameFromUpdate$ -
    // to prevent frame updates from interfering with workspace updates
    this.frameFromRoute$.pipe(
      filter(isWorkspaceFrameItem),
      map((frame) => frame.workspace),
    ),
    this.workspaceUpdate.pipe(
      // increase the running updates count
      tap(() => {
        this.workspaceUpdateCount.next(this.workspaceUpdateCount.value + 1);
        this.workspaceNoticeCount.next(this.workspaceNoticeCount.value + 1);
      }),
      // Same reason as the frame hand-off above.
      mergeMap((update) => {
        const workspace$ = this.workspacesService.updateWorkspace(update);
        this.workspaceNoticeCount.next(this.workspaceNoticeCount.value - 1);
        return workspace$.pipe(
          // decrease the running updates count
          finalize(() =>
            this.workspaceUpdateCount.next(this.workspaceUpdateCount.value - 1),
          ),
          // complete inner stream on error
          catchError(() => EMPTY),
        );
      }),
      // debounce until all updates finished
      debounce(() => this.noWorkspaceUpdates$),
      tap((workspace) => {
        if (this.frame && isWorkspaceFrameItem(this.frame)) {
          this.frame.workspace = workspace;
        }
      }),
    ),
  );

  readonly workspaceItem$: Observable<WorkspaceItem | undefined> =
    this.workspace$.pipe(
      map((workspace) =>
        workspace && isWorkspaceItem(workspace) ? workspace : undefined,
      ),
    );

  readonly changeNameDialogProperties$ = combineLatest([
    this.changeNameDialogHeaderKey$.pipe(
      switchMap((key) =>
        this.translocoService.selectTranslate(
          `modal.${key}`,
          {},
          'featureWorkspaces',
        ),
      ),
    ),
    this.translocoService.selectTranslateObject('modal', {}, 'uiComponents'),
  ]).pipe(
    map(
      ([headerTitle, footerTranslations]) =>
        new DialogProperties({
          defaultHeader: { headerTitle },
          defaultFooter: {
            footerConfirmButtonLabel: footerTranslations.saveChanges,
            footerDismissButtonLabel: footerTranslations.cancel,
          },
          defaultSize: DefaultDialogSize.SMALL,
        }),
    ),
  );

  readonly deleteFrameDialogProperties$ = this.translocoService
    .selectTranslateObject('deleteFrameModal', {}, 'featureWorkspaces')
    .pipe(
      map(
        (translations) =>
          new DialogProperties({
            defaultFooter: {
              footerConfirmButtonLabel: translations.confirm,
              footerDismissButtonLabel: translations.dismiss,
            },
            defaultSize: DefaultDialogSize.SMALL,
            defaultStyle: 'error',
          }),
      ),
    );

  readonly inviteDialogProperties$: Observable<DialogProperties> =
    this.pageData$.pipe(
      switchMap((data) =>
        this.translocoService
          .selectTranslate(
            'inviteModal.invitePeople',
            { workspaceName: data.workspace?.name },
            'featureWorkspaces',
          )
          .pipe(
            map((headerTitle) => ({
              defaultHeader: { headerTitle },
            })),
          ),
      ),
    );

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  ngOnDestroy(): void {
    this.navigationToolbarService.burgerMenuItems$.next([]);
    this.navigationToolbarService.breadcrumbItems$.next([]);
    this.navigationToolbarService.resetSearchableMenu();
    this.navigationToolbarService.showMinimal$.next(false);
    this.accountToolbarService.toolbarSecondaryButton$.next(undefined);
    this.navigationToolbarService.toolbarSecondaryButton$.next(undefined);
    this.navigationToolbarService.toolbarPrimaryButton$.next(undefined);
    this.navigationToolbarService.viewDetailsContentItem$.next(undefined);
    this.contentDetailsStore.updateFrameId(undefined);
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onContentUpdateNotice(
    notice: ToolContentUpdateNotice,
    frame: FrameBase,
  ): void {
    if (isWorkspaceFrameBase(frame)) {
      this.updateNotice.next({
        id: frame.id,
        toolType: frame.toolType,
        ...notice,
      });
    }
  }

  onElementIdsSelectionChange(elementIds: UUID[], frame: FrameBase): void {
    if (isWorkspaceFrameBase(frame)) {
      this.elementIdsSelectionSubject$.next(elementIds);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    this.bufferFlush.next();
    if (
      this.updateCount.value > 0 ||
      this.workspaceUpdateCount.value > 0 ||
      this.contentDetailsStore.numberOfUnsavedChanges() > 0
    ) {
      event.preventDefault();
      // Using deprecated returnValue api which is required for Chrome to display the browser alert dialog.
      event.returnValue = true;
    }
  }

  async canDeactivate(): Promise<boolean> {
    this.bufferFlush.next();
    // wait until all notices resolved
    // The counters, not the streams that filter to zero first: `combineLatest`
    // could pair one stale `true` with the other's current value.
    return firstValueFrom(
      combineLatest([this.noticeCount, this.workspaceNoticeCount]).pipe(
        filter(
          ([notices, workspaceNotices]) =>
            notices === 0 && workspaceNotices === 0,
        ),
        map(() => true),
      ),
    );
  }

  private async getToolContainerType(
    toolType: ToolType,
  ): Promise<Type<ToolContainer>> {
    return await this.toolSpecRegister[toolType]?.containerType();
  }

  private async getToolInputsFactory(
    toolType: ToolType,
  ): Promise<ToolInputsFactory> {
    return this.injector.get<ToolInputsFactory>(
      await this.toolSpecRegister[toolType]?.inputsFactoryType(),
    );
  }

  private reduceUpdateNotices(
    notices: ExtendedUpdateNotice[],
  ): ExtendedUpdateNotice {
    this.updateCount.next(this.updateCount.value + 1 - notices.length);
    this.noticeCount.next(this.noticeCount.value + 1 - notices.length);
    return notices.reduce(
      (prev, curr) => ({ ...prev, ...curr }),
      {} as ExtendedUpdateNotice,
    );
  }

  private resolveUpdateNotice(
    notice: ExtendedUpdateNotice,
  ): Observable<WorkspaceFrameUpdate | null> {
    const getContentUpdate = notice.getContentUpdate;
    if (!getContentUpdate) return of(notice);
    return defer(() => getContentUpdate(notice.timestamp)).pipe(
      map((contentUpdate) =>
        contentUpdate ? { ...notice, ...contentUpdate } : null,
      ),
      // A build that throws would otherwise kill this stream for the rest of the session,
      // stranding both counters so `canDeactivate` never resolves again.
      catchError(() => of(null)),
    );
  }

  private updateToolbars(frame: FrameBase, initialize = false): void {
    this.frame = frame;
    this.navigationToolbarService.viewDetailsContentItem$.next(
      this.getViewDetailsContentItem(frame),
    );
    this.pinUnpinLabelKey.next(
      isWorkspaceFrameBase(frame) && frame.pinned
        ? 'menu.unpinFrame'
        : 'menu.pinFrame',
    );
    const adHocBurgerMenuOptions = isAdhocFrameBase(frame)
      ? []
      : [...displayMenuItemsPublishedView()];
    if (isWorkspaceFrameBase(frame) && frame.workspace.archived) {
      this.navigationToolbarService.showMinimal$.next(true);
    }
    this.navigationToolbarService.breadcrumbItems$.next([
      ...(isWorkspaceFrameItem(frame)
        ? [
            {
              label: frame.workspace.name,
              command: () => {
                this.navigateToWorkspace(frame.workspace);
              },
            },
          ]
        : []),
      { label: frame.name },
    ]);
    if (!initialize) return;
    if (isWorkspaceFrameItem(frame)) {
      this.accountToolbarService.toolbarSecondaryButton$.next({
        labelKey: 'workspace.inviteButtonText',
        tScope: 'featureWorkspaces',
        type: 'secondary',
        onClick: () => {
          this.productAnalyticsService.sendEvent('workspace_invite_opened', {
            workspace_id: frame.workspace.id,
            location: 'frame_account_toolbar',
          });
          this.inviteDialogVisible$.next(true);
        },
      });
      if (!isWorkspaceFrameBase(frame) && frame.toolType === ToolType.DESIGN) {
        this.navigationToolbarService.toolbarSecondaryButton$.next({
          id: 'publish-button',
          labelKey: 'graphActions.promoteButtonTitle',
          tScope: 'featureModeler',
          disabled: true,
          onClick: () => {},
        });
      }
    }
    // TODO: use template type to determine type of frame and its burger menus
    this.navigationToolbarService.burgerMenuItems$.next(
      isWorkspaceFrameItem(frame)
        ? [
            {
              labelKey: 'menu.frame',
              tScope: 'uiComponents',
              items: frameMenuItems(frame, this.pinUnpinLabelKey).map(
                (item) => ({
                  ...item,
                  command: this.frameMenuCommandHandler(
                    frame,
                    item.labelKey ?? item.id,
                  ),
                }),
              ),
            },
            ...resolveCallbackForDesignType(frame, this.designFrameSubmenus),
            {
              separator: true,
            },
            {
              labelKey: 'menu.goToWorkspace',
              tScope: 'uiComponents',
              command: () => {
                this.navigateToWorkspace(frame.workspace);
              },
            },
          ]
        : adHocBurgerMenuOptions,
    );
  }

  private getViewDetailsContentItem(frame: FrameBase): ContentItem | undefined {
    if (this.route.snapshot.paramMap?.has('vid') !== true) {
      return undefined;
    }

    return {
      id: frame.id,
      scope: 'view',
      typeName: '',
      name: frame.name,
      icon: 'ui-view',
      metaModel: '',
    };
  }

  changeNameForFrame() {
    if (this.frame && isWorkspaceFrameBase(this.frame)) {
      this.changeNameDialogHeaderKey$.next(
        this.frame.toolType === ToolType.DESIGN
          ? 'designChangeName'
          : 'insightChangeName',
      );

      this.changeNameValue.set(this.frame.name);
      this.changeNameDialogVisible$.next(true);
    }
  }

  private resetComponent(): void {
    this.bufferFlush.next();
    this.elementIdsSelectionSubject$.next([]);
    this.changeNameDialogVisible$.next(false);
    this.contentOpenDialogVisible$.next(false);
    this.workspacePickerDialogVisible$.next(false);
    this.deleteFrameDialogVisible$.next(false);
  }

  deleteWorkspaceFrame(frameId: string, workspaceId: UUID) {
    this.workspacesService.deleteWorkspaceFrame(frameId, workspaceId);
  }

  onChangeNameConfirm(pageData: PageData) {
    if (pageData.isWorkspaceFrame) {
      this.updateNotice.next({
        id: pageData.frameId,
        toolType: pageData.toolType,
        name: this.changeNameValue(),
        forceFlush: true,
      });
    }
  }

  onDeleteFrameConfirm(frameId: string, workspace?: WorkspaceBase) {
    if (workspace) {
      this.deleteWorkspaceFrame(frameId, workspace.id);
      this.navigateToWorkspace(workspace);
    }
  }

  filterInputsForComponent<T extends object>(
    componentType: Type<T>,
    inputs: T,
  ): Partial<T> {
    const mirror = reflectComponentType(componentType);

    if (!mirror) {
      return {};
    }

    const allowedNames = new Set(
      mirror.inputs.map((input: { propName: string }) => input.propName),
    );

    const result: Partial<T> = {};

    for (const [key, value] of Object.entries(inputs)) {
      if (allowedNames.has(key)) {
        result[key as keyof T] = value;
      }
    }

    return result;
  }

  private mergeToolContainerInputs(
    inputs: Partial<ToolContainerInputs>,
    frameName: string,
  ): ToolContainerInputs {
    return {
      config: inputs.config,
      frameName,
      data: inputs.data,
      onContentUpdateNotice: inputs.onContentUpdateNotice,
      onObjectIdsInUseChange: inputs.onObjectIdsInUseChange,
      onElementIdsSelectionChange: inputs.onElementIdsSelectionChange,
      onToolEvent: inputs.onToolEvent,
    };
  }

  onChangeNameDialogVisibleChange(visible: boolean, frameId: UUID): void {
    const wasVisible = this.changeNameDialogVisible$.value;
    this.changeNameDialogVisible$.next(visible);

    if (wasVisible && !visible) {
      this.dispatcher.dispatch(
        frameToolUiEvents.graphFocusRequested({ frameId }),
      );
    }
  }

  onDeleteFrameVisibleChange(visible: boolean): void {
    this.deleteFrameDialogVisible$.next(visible);
  }

  onInviteDialogVisibleChange(visible: boolean): void {
    this.inviteDialogVisible$.next(visible);
    this.inlineNotificationVisible.set(false);
  }

  onContentOpenSelectionConfirm(choice: ContentOpenDialogChoice): void {
    if (choice.selectedTemplate !== TemplateType.DESIGN) return;

    this.dispatcher.dispatch(
      frameCreationSnackbarUiEvents.creationStarted({
        source: 'add-to-frame',
      }),
    );
  }

  onWorkspacePickerSelectionConfirm(
    pickerChoice: ContentOpenDialogChoice,
    pageData: PageData,
  ): void {
    if (pickerChoice.selectedWorkspace === newWorkspace) {
      this.dispatcher.dispatch(
        dialogWorkspacePickerUiEvents.newWorkspaceClicked(),
      );
      this.contentOpenService.openViewInNewWorkspace(pageData.frameId);
    } else {
      this.dispatcher.dispatch(
        dialogWorkspacePickerUiEvents.workspaceConfirmClicked(),
      );
      this.dispatcher.dispatch(
        frameCreationSnackbarUiEvents.creationStarted({
          source: 'add-view-to-frame',
        }),
      );
      this.contentOpenService.openViewInWorkspace(
        pageData.frameId,
        pickerChoice.selectedWorkspace,
      );
    }
  }

  protected onWorkspaceUpdate(update: WorkspaceUpdate): void {
    this.workspaceUpdate.next(update);
  }

  protected onInviteCopyLink(): void {
    this.inlineNotificationVisible.set(true);
    this.productAnalyticsService.sendEvent('copy_frame_link_clicked');
    navigator.clipboard.writeText(globalThis.location.href);
  }

  protected onInviteLinkClose(): void {
    this.inlineNotificationVisible.set(false);
  }

  navigateToWorkspace(workspace: WorkspaceBase) {
    this.router.navigate([baseRoutes.workspaces, workspace.id], {
      state: new WorkspaceNavigationState(workspace, undefined),
    });
  }

  duplicateFrame = (frame: WorkspaceFrameItem): void => {
    const isDesignFrame = frame.toolType === ToolType.DESIGN;
    const sourceContext = isDesignFrame ? 'design-frame' : undefined;

    if (isDesignFrame) {
      this.dispatcher.dispatch(
        frameCreationSnackbarUiEvents.creationStarted({
          source: 'duplicate-frame-from-design-frame',
        }),
      );
    }

    this.router.navigate([baseRoutes.frames, 'create'], {
      state: new DuplicateWorkspaceFrameNavigationState(frame, sourceContext),
    });
  };

  togglePinUnpinFrame = (frame: WorkspaceFrameItem): void => {
    if (isWorkspaceFrameBase(frame)) {
      const pinned = frame.pinned;
      frame.pinned = !pinned;
      this.pinUnpinLabelKey.next(pinned ? 'menu.pinFrame' : 'menu.unpinFrame');
      const update: WorkspaceUpdate = {
        id: frame.workspace.id,
        [pinned ? 'unpinnedFrames' : 'pinnedFrames']: { [frame.id]: frame },
      };
      this.workspaceUpdate.next(update);
    }
  };

  protected goToArchiveCTA = (): void => {
    this.router.navigate([baseRoutes.archive]);
  };

  private frameErrorHandler(error: Error): Error {
    const urlTree =
      error instanceof InvalidAssetIdError
        ? this.router.createUrlTree([baseRoutes.pageNotFound, this.router.url])
        : this.router.createUrlTree([baseRoutes.accessDenied, this.router.url]);
    this.router.navigateByUrl(urlTree);
    return error;
  }
}
